import { useEffect, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { UseMutationResult } from '@tanstack/react-query';
import type { TaskWithStats, MergeTasksResult } from '../../services/api/tasks';

interface MergeTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  sourceTasks: TaskWithStats[];
  projectTasks: TaskWithStats[];
  mergeMutation: UseMutationResult<
    MergeTasksResult,
    Error,
    { sourceTaskIds: number[]; targetTaskId: number }
  >;
}

function formatHours(hours: number): string {
  return hours.toFixed(1);
}

export function MergeTasksModal({
  isOpen,
  onClose,
  onComplete,
  sourceTasks,
  projectTasks,
  mergeMutation,
}: MergeTasksModalProps) {
  const [targetTaskId, setTargetTaskId] = useState<string>('');
  const [result, setResult] = useState<MergeTasksResult | null>(null);
  // F25-002: track the auto-close timer so unmount cancels it — otherwise
  // a parent that unmounts the modal in the 1500ms window triggers state
  // updates on an unmounted component.
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  const sourceIds = new Set(sourceTasks.map((t) => t.id));
  const availableTargets = projectTasks.filter((t) => !sourceIds.has(t.id));

  const totalEntries = sourceTasks.reduce((sum, t) => sum + t.entryCount, 0);
  const totalHours = sourceTasks.reduce((sum, t) => sum + t.totalHours, 0);

  const handleClose = () => {
    setTargetTaskId('');
    setResult(null);
    onClose();
  };

  const handleMerge = async () => {
    if (!targetTaskId) return;

    try {
      const mergeResult = await mergeMutation.mutateAsync({
        sourceTaskIds: sourceTasks.map((t) => t.id),
        targetTaskId: parseInt(targetTaskId, 10),
      });
      setResult(mergeResult);

      if (mergeResult.failed.length === 0) {
        if (autoCloseTimerRef.current !== null) {
          clearTimeout(autoCloseTimerRef.current);
        }
        autoCloseTimerRef.current = setTimeout(() => {
          autoCloseTimerRef.current = null;
          handleClose();
          onComplete();
        }, 1500);
      }
    } catch {
      // Error is handled by mutation
    }
  };

  const handleDone = () => {
    handleClose();
    onComplete();
  };

  const selectedTarget = availableTargets.find(
    (t) => t.id === parseInt(targetTaskId, 10)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Merge Tasks"
      size="md"
      footer={
        result ? (
          <div className="flex justify-end">
            <Button onClick={handleDone}>Done</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={mergeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={!targetTaskId || mergeMutation.isPending}
              isLoading={mergeMutation.isPending}
            >
              Merge {sourceTasks.length} task{sourceTasks.length !== 1 ? 's' : ''}
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          {result.merged.length > 0 && (
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <svg
                className="h-5 w-5 text-green-500 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Merged {result.merged.length} task
                  {result.merged.length !== 1 ? 's' : ''} ({result.entriesMoved} entr
                  {result.entriesMoved !== 1 ? 'ies' : 'y'} moved)
                </p>
              </div>
            </div>
          )}

          {result.failed.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-2">
                Failed to merge {result.failed.length} task
                {result.failed.length !== 1 ? 's' : ''}:
              </p>
              <ul className="text-sm text-red-700 space-y-1">
                {result.failed.map((failure) => {
                  const task = sourceTasks.find((t) => t.id === failure.id);
                  return (
                    <li key={failure.id}>
                      <span className="font-medium">{task?.name || `Task #${failure.id}`}:</span>{' '}
                      {failure.reason}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600">
            Merge{' '}
            <span className="font-medium">{sourceTasks.length}</span> task
            {sourceTasks.length !== 1 ? 's' : ''} into a target task. All entries will
            be reassigned and the source task{sourceTasks.length !== 1 ? 's' : ''} will
            be deleted.
          </p>

          {/* Source tasks preview */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Source tasks ({totalEntries} entr{totalEntries !== 1 ? 'ies' : 'y'},{' '}
              {formatHours(totalHours)} hours):
            </p>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
              <ul className="divide-y divide-gray-200">
                {sourceTasks.map((task) => (
                  <li key={task.id} className="px-3 py-2 text-sm flex justify-between">
                    <span>
                      <span className="font-medium">{task.name}</span>
                      {task.code && (
                        <span className="ml-2 text-gray-500 font-mono text-xs">
                          [{task.code}]
                        </span>
                      )}
                    </span>
                    <span className="text-gray-500">
                      {task.entryCount} / {formatHours(task.totalHours)}h
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Target task selector */}
          <div>
            <label htmlFor="merge-tasks-target" className="block text-sm font-medium text-gray-700 mb-1">
              Target Task
            </label>
            <select
              id="merge-tasks-target"
              value={targetTaskId}
              onChange={(e) => setTargetTaskId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Select a task...</option>
              {availableTargets.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name}{task.code ? ` [${task.code}]` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedTarget && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Merging into:{' '}
                <span className="font-medium">{selectedTarget.name}</span>
                {selectedTarget.code && (
                  <span className="ml-1 font-mono text-xs">[{selectedTarget.code}]</span>
                )}
                <span className="ml-2 text-blue-600">
                  ({selectedTarget.entryCount} existing entr
                  {selectedTarget.entryCount !== 1 ? 'ies' : 'y'},{' '}
                  {formatHours(selectedTarget.totalHours)}h)
                </span>
              </p>
            </div>
          )}

          {mergeMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                {(mergeMutation.error as Error)?.message || 'Failed to merge tasks'}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
